import { useRouter } from 'next/router';
import { formatISO, parseISO } from 'date-fns';
import {
	MessageFilterType,
	OrderByCaseClients,
	StatusCaseClients,
	StatusCaseClientsMap,
} from '@/interfaces';

/**
 * Raw values straight from router.query (strings).
 * Useful for queryKey arrays and anything that needs the unparsed value.
 */
export interface CaseClientRawFilters {
	search: string | undefined;
	sort: string | undefined;
	status: string | undefined;
	messageFilter: string | undefined;
	step: string | undefined;
	dateFrom: string | undefined;
	dateTo: string | undefined;
	documentFilter: string | undefined;
	claimFormQuestions: string | undefined;
	flagged: string | undefined;
	flaggedReason: string | undefined;
	documents: string | undefined;
	state: string | undefined;
	// List-only params
	clientId: string | undefined;
	segmentId: string | undefined;
	isOrderAlph: string | undefined;
	actionStatusLawyer: string | undefined;
	actionStatusClient: string | undefined;
	totalCase: string | undefined;
	totalTaskClient: string | undefined;
	totalTaskLawyer: string | undefined;
	totalMessage: string | undefined;
}

/**
 * Fully-parsed values ready to be spread into any API payload.
 * Adding a new filter here automatically makes it available to every consumer.
 */
export interface CaseClientParsedFilters {
	search: string | undefined;
	orderBy: OrderByCaseClients | undefined;
	status: StatusCaseClients | undefined;
	messageFilter: MessageFilterType | undefined;
	stepperStatusIds: number[] | undefined;
	start_date: string | undefined;
	end_date: string | undefined;
	questions:
		| {
				questionId: number;
				answers: string[];
		  }[]
		| undefined;
	claimFormQuestions:
		| {
				questionId: number;
				answers: string[];
		  }[]
		| undefined;
	flagged: boolean | undefined;
	flagged_reasons: string[] | undefined;
	documents: string[] | undefined;
	stateIds: number[] | undefined;
}

/**
 * Central hook that reads all shared case-client filter params from the URL
 * and exposes both raw (string) and parsed (typed) versions.
 *
 * @example
 * const { rawFilters, parsedFilters } = useCaseClientFilters();
 * // Use rawFilters in queryKey arrays.
 * // Spread parsedFilters into API payloads.
 */
export const useCaseClientFilters = () => {
	const router = useRouter();

	const {
		search,
		sort,
		status,
		messageFilter,
		step,
		dateFrom,
		dateTo,
		documentFilter,
		claimFormQuestions,
		flagged,
		flaggedReason,
		documents,
		state,
		// List-only
		clientId,
		segmentId,
		isOrderAlph,
		actionStatusLawyer,
		actionStatusClient,
		totalCase,
		totalTaskClient,
		totalTaskLawyer,
		totalMessage,
	} = router.query;

	const rawFilters: CaseClientRawFilters = {
		search: search as string | undefined,
		sort: sort as string | undefined,
		status: status as string | undefined,
		messageFilter: messageFilter as string | undefined,
		step: step as string | undefined,
		dateFrom: dateFrom as string | undefined,
		dateTo: dateTo as string | undefined,
		documentFilter: documentFilter as string | undefined,
		claimFormQuestions: claimFormQuestions as string | undefined,
		flagged: flagged as string | undefined,
		flaggedReason: flaggedReason as string | undefined,
		documents: documents as string | undefined,
		state: state as string | undefined,
		clientId: clientId as string | undefined,
		segmentId: segmentId as string | undefined,
		isOrderAlph: isOrderAlph as string | undefined,
		actionStatusLawyer: actionStatusLawyer as string | undefined,
		actionStatusClient: actionStatusClient as string | undefined,
		totalCase: totalCase as string | undefined,
		totalTaskClient: totalTaskClient as string | undefined,
		totalTaskLawyer: totalTaskLawyer as string | undefined,
		totalMessage: totalMessage as string | undefined,
	};

	const parsedFilters: CaseClientParsedFilters = {
		search: (search as string) || undefined,

		orderBy: (sort as OrderByCaseClients) || undefined,

		status: StatusCaseClientsMap[status as StatusCaseClients] || undefined,

		messageFilter: (messageFilter as MessageFilterType) || undefined,

		stepperStatusIds: step
			? (step as string).split(',').map(Number)
			: undefined,

		start_date: dateFrom ? formatISO(parseISO(dateFrom as string)) : undefined,

		end_date: dateTo ? formatISO(parseISO(dateTo as string)) : undefined,

		questions: documentFilter
			? (JSON.parse(documentFilter as string) as {
					questionId: number;
					answers: string[];
			  }[])
			: undefined,

		claimFormQuestions: claimFormQuestions
			? (JSON.parse(claimFormQuestions as string) as {
					questionId: number;
					answers: string[];
			  }[])
			: undefined,

		flagged: flaggedReason
			? true
			: flagged === 'true'
			? true
			: flagged === 'false'
			? false
			: undefined,

		flagged_reasons: flaggedReason
			? (flaggedReason as string).split(',')
			: undefined,

		documents: documents ? (documents as string).split(',') : undefined,

		stateIds: state ? (state as string).split(',').map(Number) : undefined,
	};

	return { rawFilters, parsedFilters };
};
